import { STATISTIC_TYPE } from "types/ReportingDashboard/elements.types"
import { DEFAULT_HEADERS, DEFAULT_CHART_LABELS, TOTALS_KEY } from "constants/tableConversion"
import {
  RawInputData,
  TransformedTableData,
  BarChartDataItem,
  PieChartSlice,
  RawDataCell,
  RawDataRow,
} from "types/tableConversion.types"
import {
  isStatisticType,
  isPercentageStatistic,
  convertToPercentage,
  sortKeysNumerically,
} from "../tableConversionHelpers"
import { processHeaders } from "../headerProcessor"

/**
 * Handles the special edge case of 2-row tables with null categories
 */
const handleSpecialEdgeCase = (data: RawInputData): TransformedTableData | null => {
  const [headersRow, secondRow] = data.data

  const isSpecialCase =
    data.data.length === 2 &&
    headersRow[0] === null &&
    headersRow[1] === "Statistic" &&
    secondRow[0] === null &&
    isStatisticType(secondRow[1]) &&
    secondRow.length >= 3

  if (!isSpecialCase) return null

  const statisticType = secondRow[1] as STATISTIC_TYPE
  const value = parseFloat(String(secondRow[2])) || 0
  const categoryName = `Data (${statisticType})`

  return {
    headers: [
      { header: DEFAULT_HEADERS.CATEGORY, subHeaders: [] },
      { header: DEFAULT_HEADERS.STATISTIC, subHeaders: [] },
      { header: DEFAULT_HEADERS.VALUE, subHeaders: [] },
    ],
    rows: [[categoryName, statisticType, value]],
    hasSubHeaders: false,
    barChartData: {
      keys: [DEFAULT_CHART_LABELS.VALUE],
      data: [
        {
          xValue: categoryName,
          [DEFAULT_CHART_LABELS.VALUE]: {
            [statisticType]: value,
          },
        },
      ],
      xAxisLabel: DEFAULT_HEADERS.CATEGORY,
      statisticTypes: [statisticType],
    },
    pieChartData: {
      categoryData: [
        {
          statistic: statisticType,
          data: [{ id: categoryName, label: categoryName, value }],
        },
      ],
      subCategoryData: [
        {
          statistic: statisticType,
          data: [{ id: DEFAULT_CHART_LABELS.VALUE, label: DEFAULT_CHART_LABELS.VALUE, value }],
        },
      ],
      statisticTypes: [statisticType],
    },
  }
}

/**
 * Finds the index of the statistic column by looking at actual data values
 * This is more reliable than checking header labels since "Statistic" might
 * be used as a label for other columns (like sub-category columns)
 */
const findStatisticColumnIndexFromData = (dataRows: RawDataRow[]): number => {
  // Look through data rows to find which column contains statistic type values
  for (const row of dataRows) {
    for (let i = 0; i < row.length; i++) {
      const cell = row[i]
      // Check if this cell contains a statistic type (n, row%, col%, etc.)
      if (isStatisticType(cell) || isPercentageStatistic(cell)) {
        return i
      }
    }
  }
  // Default to index 1 for backward compatibility
  return 1
}

/**
 * Processes table rows with percentage formatting where applicable
 */
const processTableRows = (
  dataRows: RawDataRow[],
  statisticColumnIndex: number
): { rows: (string | number | null | STATISTIC_TYPE)[][], statisticTypes: Set<STATISTIC_TYPE> } => {
  const statisticTypes = new Set<STATISTIC_TYPE>()

  const rows = dataRows.map(row => {
    // Use the correct column for statistic/percentage detection
    const statisticCell = row[statisticColumnIndex]
    const isPercentage = isPercentageStatistic(statisticCell)

    // Keep original output structure: [firstColumn, typedStatistic, ...valueCells]
    // Apply percentage formatting to value cells (after position 1) when detected
    const processedValueCells = row
      .slice(2)
      .map(cell => isPercentage ? convertToPercentage(cell) : cell)

    // For statistic typing, use row[1] to maintain original output structure
    // Handle null values to avoid converting them to the string "null"
    const typedStatistic = row[1] === null
      ? null
      : isStatisticType(row[1])
        ? (row[1] as STATISTIC_TYPE)
        : String(row[1])

    if (isStatisticType(row[1])) {
      statisticTypes.add(row[1] as STATISTIC_TYPE)
    }

    const firstColumn = row[0] !== null ? row[0] : null
    return [firstColumn, typedStatistic, ...processedValueCells]
  })

  return { rows, statisticTypes }
}

/**
 * Processes bar chart data from table rows
 */
const processBarChartData = (
  dataRows: RawDataRow[],
  headersRow: RawDataRow,
  subHeadersRow: RawDataRow,
  isSubHeaderPresent: boolean
): { data: BarChartDataItem[], keys: string[] } => {
  const tempBarChartData: { [category: string]: BarChartDataItem } = {}
  let currentCategory: RawDataCell = null

  dataRows.forEach(row => {
    if (row[0] !== null) {
      currentCategory = row[0]
    } else if (currentCategory === null) {
      const statisticString = String(row[1] || "")
      currentCategory = isStatisticType(statisticString)
        ? `Data (${statisticString})`
        : headersRow[0] || "Data"
    }

    if (currentCategory !== null) {
      const categoryKey = String(currentCategory)
      if (!tempBarChartData[categoryKey]) {
        tempBarChartData[categoryKey] = { xValue: currentCategory }
      }

      const valueHeaders = isSubHeaderPresent
        ? subHeadersRow.slice(2)
        : headersRow.slice(2)

      valueHeaders.forEach((header, headerIndex) => {
        const headerKey = String(header || "Value")
        if (!tempBarChartData[categoryKey][headerKey]) {
          tempBarChartData[categoryKey][headerKey] = {}
        }

        const cellValue = parseFloat(String(row[headerIndex + 2]))
        const statisticString = String(row[1] || "")

        if (isStatisticType(statisticString)) {
          const statData = tempBarChartData[categoryKey][headerKey] as {
            [statistic in STATISTIC_TYPE]?: number
          }
          statData[statisticString as STATISTIC_TYPE] = !isNaN(cellValue) ? cellValue : 0
        }
      })
    }
  })

  const rawKeys = (isSubHeaderPresent ? subHeadersRow.slice(2) : headersRow.slice(2))
    .map((key, index) => String(key || `Value${index > 0 ? index + 1 : ""}`))

  return {
    data: Object.values(tempBarChartData),
    keys: sortKeysNumerically(rawKeys),
  }
}

/**
 * Creates pie chart data from bar chart data
 */
const createPieChartData = (
  barChartData: BarChartDataItem[],
  keys: string[],
  statisticTypes: STATISTIC_TYPE[]
) => {
  const categoryPieData: { [statistic in STATISTIC_TYPE]?: PieChartSlice[] } = {}
  const subCategoryPieData: { [statistic in STATISTIC_TYPE]?: PieChartSlice[] } = {}

  statisticTypes.forEach(statistic => {
    categoryPieData[statistic] = []
    subCategoryPieData[statistic] = []

    if (barChartData.length === 1) {
      // Single-row cross-tab
      const singleRowData = barChartData[0]
      keys.forEach(key => {
        if (key !== TOTALS_KEY) {
          const keyData = singleRowData[key] as { [stat in STATISTIC_TYPE]?: number }
          if (keyData && keyData[statistic] !== undefined) {
            subCategoryPieData[statistic]!.push({
              id: key,
              label: key,
              value: keyData[statistic]!,
            })
          }
        }
      })
    } else {
      // Multi-row logic
      barChartData.forEach(categoryItem => {
        if (categoryItem.xValue !== TOTALS_KEY) {
          const totalsForStatistic = (categoryItem[TOTALS_KEY] as { [stat in STATISTIC_TYPE]?: number })?.[statistic]
          if (totalsForStatistic !== undefined && categoryPieData[statistic]) {
            categoryPieData[statistic]!.push({
              id: categoryItem.xValue,
              label: categoryItem.xValue,
              value: totalsForStatistic,
            })
          }
        }
      })

      const subCategoriesForPie = keys.filter(key => key !== TOTALS_KEY)
      subCategoriesForPie.forEach(subCategory => {
        let totalForSubCategory = 0
        barChartData.forEach(categoryItem => {
          if (categoryItem.xValue !== TOTALS_KEY) {
            const subCatData = categoryItem[subCategory] as { [stat in STATISTIC_TYPE]?: number }
            if (subCatData && subCatData[statistic] !== undefined) {
              totalForSubCategory += subCatData[statistic]!
            }
          }
        })
        if (subCategoryPieData[statistic]) {
          subCategoryPieData[statistic]!.push({
            id: subCategory,
            label: subCategory,
            value: totalForSubCategory,
          })
        }
      })
    }
  })

  return { categoryPieData, subCategoryPieData }
}

/**
 * Enhanced XTAB conversion that handles edge cases including null categories
 */
export const convertXTABMultiToChart = (data: RawInputData): TransformedTableData => {
  // Handle special edge case
  const specialCaseResult = handleSpecialEdgeCase(data)
  if (specialCaseResult) return specialCaseResult

  const headersRow = data.data[0] || []
  const secondRow = data.data[1] || []

  const isSubHeaderPresent =
    secondRow.length > 1 &&
    secondRow[1] === "Statistic" &&
    secondRow.some((cell, index) => index > 1 && cell !== null && typeof cell === "string")

  const subHeadersRow = isSubHeaderPresent ? secondRow : []
  const dataRows = isSubHeaderPresent ? data.data.slice(2) : data.data.slice(1)

  // Determine the statistic column index by analyzing actual data values
  const statisticColumnIndex = findStatisticColumnIndexFromData(dataRows)

  // Process headers
  const headers = processHeaders(headersRow, subHeadersRow, isSubHeaderPresent)

  // Process table rows
  const { rows, statisticTypes } = processTableRows(dataRows, statisticColumnIndex)

  // Process bar chart data
  const { data: barChartData, keys } = processBarChartData(
    dataRows,
    headersRow,
    subHeadersRow,
    isSubHeaderPresent
  )

  // Create pie chart data
  const statisticTypesArray = Array.from(statisticTypes)
  const { categoryPieData, subCategoryPieData } = createPieChartData(
    barChartData,
    keys,
    statisticTypesArray
  )

  const firstHeaderValue = String(headersRow[0] || DEFAULT_HEADERS.CATEGORY)
  const xAxisLabel = firstHeaderValue === "null" ? "Data" : firstHeaderValue

  return {
    headers,
    rows,
    hasSubHeaders: isSubHeaderPresent,
    barChartData: {
      keys,
      data: barChartData,
      xAxisLabel,
      statisticTypes: statisticTypesArray,
    },
    pieChartData: {
      categoryData: Object.entries(categoryPieData).map(([statistic, data]) => ({
        statistic: statistic as STATISTIC_TYPE,
        data: data!,
      })),
      subCategoryData: Object.entries(subCategoryPieData).map(([statistic, data]) => ({
        statistic: statistic as STATISTIC_TYPE,
        data: data!,
      })),
      statisticTypes: statisticTypesArray,
    },
  }
}